import {
  STATISTICS_DISTRIBUTION_LABELS,
  type StatisticsDistribution,
  type StatisticsDistributionRow,
} from '@easyhelp/shared';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useId } from 'react';
import { displayRows } from './distribution-rows';
import { NO_DATA, formatShare } from './format';

export interface DistributionCardProps {
  dimension: StatisticsDistribution;
  rows: StatisticsDistributionRow[];
  /** Base delle quote: i ticket lavorabili del periodo. */
  total: number;
}

/**
 * Una distribuzione come tabella (voce, ticket, quota) con una barra per riga. La barra è solo un
 * aiuto visivo, nascosta alle tecnologie assistive: il dato è nel testo della tabella. Le schermate
 * oltre le prime dieci sono sommate in una riga «Altre N schermate».
 */
export function DistributionCard({ dimension, rows, total }: DistributionCardProps) {
  const titleId = useId();
  const { title, subtitle } = STATISTICS_DISTRIBUTION_LABELS[dimension];
  const shown = displayRows(dimension, rows);
  const max = Math.max(0, ...shown.map((row) => row.count));
  const empty = shown.every((row) => row.count === 0);

  return (
    <Paper component="section" variant="outlined" aria-labelledby={titleId} sx={{ p: 2.5 }}>
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography id={titleId} variant="h6" component="h2">
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </Stack>
      {empty ? (
        <Typography color="text.secondary">{NO_DATA}</Typography>
      ) : (
        <Table size="small" aria-labelledby={titleId}>
          <TableHead>
            <TableRow>
              <TableCell>Voce</TableCell>
              <TableCell align="right">Ticket</TableCell>
              <TableCell align="right">Quota</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((row) => (
              <TableRow key={row.other ? 'altre' : (row.key ?? `assente:${row.label}`)}>
                <TableCell sx={{ width: '100%' }}>
                  <Typography
                    variant="body2"
                    color={row.key === null ? 'text.secondary' : 'text.primary'}
                    sx={row.other ? { fontStyle: 'italic' } : undefined}
                  >
                    {row.label}
                  </Typography>
                  <LinearProgress
                    aria-hidden
                    variant="determinate"
                    value={max === 0 ? 0 : (row.count / max) * 100}
                    sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
                  />
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {row.count}
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {formatShare(row.count, total) ?? '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}
